import { isPageName } from '../validators/page-name.validator.js'

/**
 * Which addresses on csvenueview.com belong to the public site, and which to the
 * app (docs/public-site.spec.md, "One address, split by path").
 *
 * The two share one domain. The app's Worker runs a small front door for the
 * site's addresses only and hands them to the site's Worker; everything else is
 * the app, served as it always was. This function is that decision, in one
 * place: the front door calls it, a spec checks the root `wrangler.toml` sends
 * every one of these to the front door, and another checks no route in the
 * app's router answers to one. A new page on either side that forgets the
 * other fails a test rather than a visitor.
 *
 * The app keeps `/venues/<id>/…`: an id is never a page name, so the two can
 * share the `/venues/` prefix. Only the bare lists moved (`/venues` to
 * `/venues/all`, the platform admin's vendor pages to `/admin/vendors`).
 */

/** The site's pages. */
export const SITE_PAGES = ['/', '/features', '/vendors', '/vendors/all', '/venues', '/contact'] as const

/** Files the site serves at the root of the domain. */
export const SITE_FILES = ['/robots.txt', '/sitemap.xml'] as const

/** Where the site's own stylesheet, scripts, fonts and images live. */
export const SITE_ASSET_PREFIX = '/_site/'

/** Sections whose children are named pages: `/venues/<name>` now, `/vendors/<name>` with vendor pages. */
export const SITE_NAMED_SECTIONS = ['venues', 'vendors'] as const

function includes<T extends string>(list: readonly T[], value: string): value is T {
  return list.some((item) => item === value)
}

/** True when the public site answers `pathname`. One trailing slash is allowed; the site redirects it away. */
export function isSitePath(pathname: string): boolean {
  const path = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
  if (includes(SITE_PAGES, path) || includes(SITE_FILES, path)) return true
  if (path.startsWith(SITE_ASSET_PREFIX)) return true
  const segments = path.split('/').slice(1)
  const [section, name] = segments
  return segments.length === 2 && section !== undefined && name !== undefined && includes(SITE_NAMED_SECTIONS, section) && isPageName(name)
}
